// Decides whether a service worker controls this page (DESIGN §5.1). Media only ever loads through
// the worker; without one the page shows a banner instead, after at most one automatic reload.
export const RELOAD_GUARD_KEY = "kinlo-sw-reload";

/** Blocked storage (all cookies blocked, say) throws on the property read itself. */
function sessionStorageOrNull() {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function waitForControl(container, timeoutMs) {
  if (container.controller) return Promise.resolve(true);
  return new Promise((resolve) => {
    const finish = (controlled) => {
      clearTimeout(timer);
      container.removeEventListener("controllerchange", onChange);
      resolve(controlled);
    };
    const onChange = () => finish(true);
    const timer = setTimeout(() => finish(Boolean(container.controller)), timeoutMs);
    container.addEventListener("controllerchange", onChange);
  });
}

function clearGuard(storage) {
  try {
    storage?.removeItem(RELOAD_GUARD_KEY);
  } catch {
    // Storage is blocked, so there is no guard to clear.
  }
}

/**
 * `beforeReload` resolves once nothing a reload would cut off is in flight (an invite being redeemed);
 * it is only asked when a reload is about to happen, and a failure in it never blocks the reload.
 * → "sw" | { noSw: "unsupported" | "registration_failed" | "uncontrolled_after_reload" }
 */
export async function initMediaMode({
  container = globalThis.navigator?.serviceWorker,
  storage = sessionStorageOrNull(),
  reload = () => globalThis.location.reload(),
  timeoutMs = 3000,
  beforeReload = () => Promise.resolve(),
} = {}) {
  if (!container) return { noSw: "unsupported" };
  try {
    await container.register("/sw.js", { scope: "/" });
  } catch {
    return { noSw: "registration_failed" };
  }
  if (await waitForControl(container, timeoutMs)) {
    clearGuard(storage);
    return "sw";
  }
  let alreadyReloaded;
  try {
    alreadyReloaded = storage.getItem(RELOAD_GUARD_KEY) !== null;
    if (!alreadyReloaded) storage.setItem(RELOAD_GUARD_KEY, "1");
  } catch {
    return { noSw: "uncontrolled_after_reload" };
  }
  if (alreadyReloaded) return { noSw: "uncontrolled_after_reload" };
  try {
    await beforeReload();
  } catch {
    // Whatever the hook was waiting on is over; the reload is still needed.
  }
  // The worker may have taken control while the reload was held back.
  if (container.controller) {
    clearGuard(storage);
    return "sw";
  }
  reload();
  return new Promise(() => {});
}
